// Layout component that hosts the desktop chrome (Sidebar + Topbar +
// CommandPalette) and renders the active screen in <Outlet />. Mounted
// behind RequireAuth, so by construction `session` is non-null inside.
//
// The Shell fires the /facility-dashboard query on mount. Every nested
// screen consumes the same QueryCache entry via useFacilityDashboard()
// — TanStack Query dedupes the second call to the same key. This is
// the "load once, read many" pattern the prototype's window.DATA
// global emulated.

import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { isFacilityDashboardEnabled, lacksDashboardSurface } from "../lib/capabilities";
import { useFacilityDashboard, useRecipe } from "../lib/api/queries";
import { CommandPalette } from "./CommandPalette";
import { NoDashboardSurface } from "./NoDashboardSurface";
import { RouteAnnouncer } from "./RouteAnnouncer";
import { Sidebar } from "./Sidebar";
import { ToastProvider } from "./ToastZone";
import { Topbar } from "./Topbar";

export function Shell() {
  const [cmdkOpen, setCmdkOpen] = useState(false);
  const { data: recipe } = useRecipe();
  // A loaded recipe with no dashboard surface (residential_rental today):
  // the shell keeps the sidebar (platform block + logout only — its nav
  // resolves to []) and renders NoDashboardSurface instead of the routed
  // screen. No breadcrumbs, no ⌘K palette, no route announcer: there is
  // nothing to navigate to. A still-loading recipe is NOT this state.
  const noSurface = lacksDashboardSurface(recipe);

  // Cmd+K / Ctrl+K opens the palette globally — not when there is no
  // surface (the palette isn't mounted then).
  useEffect(() => {
    if (noSurface) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setCmdkOpen((v) => !v);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [noSurface]);

  // Fire the dashboard query at the shell level so every screen
  // reads from one QueryCache entry. The Shell itself doesn't
  // render the result — Sidebar reads counts off it, the screens
  // re-call the same hook. Gated on the recipe's facility capability —
  // TanStack Query's `enabled` is per-observer, so Sidebar and
  // CommandPalette (also mounted here) must repeat this same gate or
  // the request still fires for non-facility orgs.
  useFacilityDashboard(isFacilityDashboardEnabled(recipe));

  // ToastProvider renders the one persistent aria-live toast region for
  // every screen (components/ToastZone.tsx); screens call useToast().
  return (
    <ToastProvider>
      <div className="app">
        {!noSurface && <RouteAnnouncer />}
        <div className="app__sidebar">
          <Sidebar />
        </div>
        <div className="app__topbar">
          {/* Bare header keeps the grid's topbar row when there is no surface. */}
          {noSurface ? (
            <header className="topbar" />
          ) : (
            <Topbar onOpenCmdk={() => setCmdkOpen(true)} />
          )}
        </div>
        <main className="app__main">{noSurface ? <NoDashboardSurface /> : <Outlet />}</main>
        {!noSurface && <CommandPalette open={cmdkOpen} onClose={() => setCmdkOpen(false)} />}
      </div>
    </ToastProvider>
  );
}
